import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  HeroCard,
  ListCard,
  Row,
  SectionHeader,
} from '@gonka-wallet/ui-tokens';
import { shortenAddress } from '@gonka-wallet/crypto';
import { WalletError } from '@gonka-wallet/chain';
import { useWalletStore } from '../stores/walletStore.js';
import { useWatchedModalStore } from '../stores/watchedModalStore.js';
import { useAggregateBalance } from '../hooks/useAggregateBalance.js';
import { useVestingAggregate } from '../hooks/useVestingAggregate.js';
import { useHistory } from '../hooks/useHistory.js';
import { useEpoch } from '../hooks/useEpoch.js';
import type { EpochInfo } from '../lib/minerApi.js';
import { useToastStore } from '../stores/toastStore.js';
import { formatGnkDisplay } from '../lib/format.js';
import { ExpandedWalletRow } from '../components/ExpandedWalletRow.js';
import { WalletGlyph } from '../components/WalletGlyph.js';
import { SendIcon, ReceiveIcon } from '../components/icons.js';

export function Wallet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const activeId = useWalletStore((s) => s.activeId);
  const setActive = useWalletStore((s) => s.setActive);
  const pushToast = useToastStore((s) => s.push);
  const openWatchedModal = useWatchedModalStore((s) => s.open);

  const addresses = useMemo(
    () => [
      ...wallets.map((w) => w.addresses[0]).filter((a): a is string => !!a),
      ...watched.map((w) => w.address),
    ],
    [wallets, watched],
  );
  const agg = useAggregateBalance(addresses);
  const vesting = useVestingAggregate(addresses);
  const epoch = useEpoch();
  const activeWallet = wallets.find((w) => w.id === activeId) ?? wallets[0];
  const activeWatched = watched.find((w) => w.id === activeId);
  const activeAddress =
    activeWallet?.addresses[0] ?? activeWatched?.address ?? null;
  const historyQuery = useHistory(activeAddress);

  useEffect(() => {
    if (!agg.error) return;
    const msg =
      agg.error instanceof WalletError
        ? t(agg.error.i18nKey)
        : t('errors.network.allDown');
    pushToast(msg, 'error');
  }, [agg.error, t, pushToast]);

  const totalRowCount = wallets.length + watched.length;
  const [openIdx, setOpenIdx] = useState<number>(totalRowCount === 1 ? 0 : -1);
  useEffect(() => {
    setOpenIdx(totalRowCount === 1 ? 0 : -1);
  }, [totalRowCount]);

  const recentThree = (historyQuery.data?.items ?? []).slice(0, 3);

  const rows = useMemo(() => {
    type R =
      | { kind: 'full'; id: string; name: string; address: string }
      | { kind: 'watch'; id: string; name: string; address: string };
    const out: R[] = [];
    for (const w of wallets) {
      const addr = w.addresses[0];
      if (!addr) continue;
      out.push({ kind: 'full', id: w.id, name: w.name, address: addr });
    }
    for (const w of watched) {
      out.push({ kind: 'watch', id: w.id, name: w.name, address: w.address });
    }
    return out;
  }, [wallets, watched]);

  const handleToggle = (idx: number, walletId: string) => {
    setOpenIdx((cur) => (cur === idx ? -1 : idx));
    setActive(walletId);
  };

  const handleSend = (walletId: string) => {
    setActive(walletId);
    navigate('/send');
  };
  const handleReceive = (walletId: string) => {
    setActive(walletId);
    navigate('/receive');
  };
  const handleImport = () => {
    navigate('/import');
  };

  const showEmpty =
    !agg.isLoading && !vesting.isLoading && !agg.error && recentThree.length === 0;

  return (
    <div className="flex min-h-screen flex-col gap-4 bg-bg p-4 text-text-primary">
      <div className="flex items-center justify-between pt-1">
        <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
          {t('app.name')}
        </div>
        <EpochStrip epoch={epoch.data} />
      </div>

      <HeroCard
        title={t('wallet.totalBalance')}
        balanceNgonka={vesting.total !== '0' ? vesting.total : agg.total}
        spendableNgonka={vesting.unlocked !== '0' ? vesting.unlocked : agg.spendable}
        vestingNgonka={vesting.locked}
        formatNgonka={formatGnkDisplay}
        isLoading={agg.isLoading || vesting.isLoading}
        collapsible
        labels={{
          vestingUnlocked: t('wallet.unlocked'),
          vestingInProgress: t('wallet.vestingInProgress'),
        }}
      />

      {rows.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionHeader
            trailing={
              <button
                type="button"
                onClick={openWatchedModal}
                className="text-[11px] font-medium text-link"
              >
                + {t('wallet.add')}
              </button>
            }
          >
            {t('wallet.myWallets')} · {rows.length}
          </SectionHeader>
          <div className="flex flex-col gap-2">
            {rows.map((r, i) => {
              const bankEntry = agg.perAddress.get(r.address);
              const bankBal = bankEntry?.balance;
              const vestEntry = vesting.perAddress.get(r.address);
              // Prefer vesting API (full total incl. locked tranches); fall
              // back to bank balance (spendable only) when the miner API
              // hasn't answered yet.
              const hasVestData =
                vestEntry !== undefined &&
                (vestEntry.total !== '0' ||
                  vestEntry.unlocked !== '0' ||
                  vestEntry.locked !== '0');
              const totalNg = hasVestData
                ? vestEntry!.total
                : bankBal?.total ?? '0';
              const spendableNg = hasVestData
                ? vestEntry!.unlocked
                : bankBal?.spendable ?? '0';
              const vestingNg = hasVestData
                ? vestEntry!.locked
                : bankBal?.vesting ?? '0';
              const hasVesting =
                vestingNg !== '0' &&
                (() => {
                  try { return BigInt(vestingNg) > 0n; } catch { return false; }
                })();

              return (
                <ExpandedWalletRow
                  key={`${r.kind}-${r.id}`}
                  id={r.id}
                  name={r.name}
                  address={r.address}
                  total={formatGnkDisplay(totalNg)}
                  glyph={<WalletGlyph seed={r.name + r.address} />}
                  unlocked={hasVesting ? formatGnkDisplay(spendableNg) : undefined}
                  vesting={hasVesting ? formatGnkDisplay(vestingNg) : undefined}
                  active={r.kind === 'full' && r.id === activeId}
                  watch={r.kind === 'watch'}
                  open={openIdx === i}
                  onToggle={() => handleToggle(i, r.id)}
                  onImportRequest={r.kind === 'watch' ? handleImport : undefined}
                  labels={{
                    unlockedLabel: t('wallet.unlocked'),
                    vestingLabel: t('wallet.vestingInProgress'),
                    totalLabel: t('wallet.total'),
                    watchOnlyHint: t('wallet.watchOnlyHint'),
                    importCta: t('wallet.importCta'),
                    activeLabel: t('wallet.active'),
                    watchLabel: t('watchedAddress.watchBadge'),
                    vestingChipLabel: t('wallet.vestingInProgress'),
                  }}
                  injectActions={
                    r.kind === 'full' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="primary" onClick={() => handleSend(r.id)} className="gap-2">
                          <SendIcon />
                          {t('home.send')}
                        </Button>
                        <Button variant="secondary" onClick={() => handleReceive(r.id)} className="gap-2">
                          <ReceiveIcon />
                          {t('home.receive')}
                        </Button>
                      </div>
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <SectionHeader
          trailing={
            recentThree.length > 0 ? (
              <button
                type="button"
                onClick={() => navigate('/history')}
                className="text-[11px] font-medium text-link"
              >
                {t('wallet.seeAll')}
              </button>
            ) : undefined
          }
        >
          {t('wallet.recent')}
        </SectionHeader>

        {historyQuery.isLoading ? (
          <ListCard>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]"
              >
                <div>
                  <div className="h-3 w-20 animate-pulse rounded bg-surface-2" />
                  <div className="mt-1.5 h-2 w-32 animate-pulse rounded bg-surface-2" />
                </div>
                <div className="h-3 w-16 animate-pulse rounded bg-surface-2" />
              </div>
            ))}
          </ListCard>
        ) : historyQuery.error ? (
          <Card className="flex flex-col items-center gap-2 p-6 text-center">
            <div className="text-[13px] font-semibold text-text-primary">
              {t('wallet.loadingError')}
            </div>
            <button
              type="button"
              onClick={() => historyQuery.refetch()}
              className="text-[11px] font-medium text-link"
            >
              {t('wallet.retry')}
            </button>
          </Card>
        ) : showEmpty ? (
          <Card className="flex flex-col items-center gap-2 p-6 text-center">
            <div className="text-[13px] font-semibold text-text-primary">
              {t('wallet.emptyActivityTitle')}
            </div>
            <div className="max-w-xs text-[11px] text-text-secondary">
              {t('wallet.emptyActivityBody')}
            </div>
          </Card>
        ) : (
          <ListCard>
            {recentThree.map((tx) => {
              const isIncoming = tx.direction === 'received';
              return (
                <Row
                  key={tx.hash}
                  label={isIncoming ? t('wallet.received') : t('wallet.sent')}
                  meta={shortenAddress(tx.counterparty)}
                  value={`${isIncoming ? '+' : '\u2212'}${formatGnkDisplay(tx.amount)} GNK`}
                  valueTone={isIncoming ? 'pos' : 'neg'}
                />
              );
            })}
          </ListCard>
        )}
      </section>
    </div>
  );
}

function EpochStrip({ epoch }: { epoch: EpochInfo | undefined }) {
  if (!epoch) return null;
  const phase = epoch.epoch_timing?.epoch_phase;
  const progressRaw = epoch.epoch_timing?.epoch_progress;
  const progress =
    typeof progressRaw === 'number' && Number.isFinite(progressRaw)
      ? Math.round(progressRaw > 1 ? progressRaw : progressRaw * 100)
      : null;
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
      <span className="font-mono tabular-nums">E·{epoch.epoch_id}</span>
      {phase && (
        <>
          <span aria-hidden className="text-text-disabled">·</span>
          <span>{phase}</span>
        </>
      )}
      {progress !== null && (
        <>
          <span aria-hidden className="text-text-disabled">·</span>
          <span className="font-mono tabular-nums">{progress}%</span>
        </>
      )}
    </div>
  );
}

